import { Icon } from '@hologram-dimension/icon';
import { Link } from '@hologram-dimension/link';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import clsx from 'clsx/lite';
import useUserInfo from 'hooks/User/useUserInfo';
import { ReactNode, useEffect, useMemo, type FC } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import globalStyles from 'styles/shared.module.css';
import {
  EnumSimInventoryExportType,
  exportTypeToLabel,
  type HologramSimIdsMap,
  type SimInventorySegmentName,
} from 'types/common';
import { useSimInventoryFilters } from 'hooks';
import styles from './ExportSims.module.css';
import useExportSims from './useExportSims';

interface ExportSimsProps {
  className?: string;
  sims: HologramSimIdsMap;
  exportType: EnumSimInventoryExportType;
  segmentName: SimInventorySegmentName;
}

const ExportSims: FC<ExportSimsProps> = ({ className, sims, exportType, segmentName }) => {
  const { isSuperAdmin } = useUserInfo();
  const { error, loadingSims, processingExport } = useExportSims({
    sims,
    exportType,
    segmentName,
    isSuperAdmin,
  });
  const { sendAnalyticsEvent } = useAnalytics();
  const { hasUserFilterApplied } = useSimInventoryFilters({ segmentName });

  const exportTypeLabel = exportTypeToLabel(exportType);

  useEffect(() => {
    if (sendAnalyticsEvent && exportTypeLabel) {
      sendAnalyticsEvent(`SIMs - ${segmentName} - Export SIMs - Export ${exportTypeLabel}`, {
        filtered: hasUserFilterApplied,
        simCount: sims.size,
      });
    }
  }, [exportTypeLabel, segmentName, sendAnalyticsEvent, hasUserFilterApplied, sims]);

  const content: ReactNode = useMemo(() => {
    if (processingExport || loadingSims) {
      return (
        <PanelNotification
          label={`Processing ${exportTypeLabel} export...`}
          className={clsx(styles.notification, globalStyles.sharedLoadingAnimation)}
        >
          Please keep this modal open. Your export is being prepared and will download automatically
          in your browser once it’s ready. This could take some time to complete.
        </PanelNotification>
      );
    }

    if (error !== null) {
      return (
        <PanelNotification
          variant="error"
          label="Error processing export"
          className={styles.notification}
        >
          <>
            We ran into an issue processing your export. If this issue persists, please{' '}
            <Link
              href="https://support.hologram.io/hc/en-us/requests/new"
              target="_blank"
              rel="noreferrer"
              type="inline"
              className={styles.contactSupportLink}
            >
              contact support
            </Link>
            .
          </>
        </PanelNotification>
      );
    }

    return (
      <PanelNotification
        label="Export complete"
        icon={<Icon name="CircleCheckmark" fill="DdsColorFillNotificationSuccess" size="large" />}
        className={styles.notification}
      >
        Check your browser downloads to view the file.
      </PanelNotification>
    );
  }, [error, exportTypeLabel, loadingSims, processingExport]);

  return <div className={clsx(styles.wrapper, className)}>{content}</div>;
};

export default ExportSims;
